import React, { useEffect, useState } from 'react';
import CourseProgressModal from './CourseProgressModal';
import ConfirmDialog from './ConfirmDialog';
import '../styles/ManageStudentModal.css';

export default function ManageStudentModal({ 
  isOpen, 
  onClose, 
  student,
  tutorId,
  courses = [],
  drafts = [],
  onAssignCourse,
  onOpenDraft,
  onRemoveCourse,
  onViewCourse,
  getAvatarUrl
}) {
  const [viewingCourse, setViewingCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('assigned');
  const [courseToRemove, setCourseToRemove] = useState(null);
  const [isRemovingCourse, setIsRemovingCourse] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('assigned');
    }
  }, [isOpen, student?.student_id]);

  useEffect(() => {
    if (!isOpen) {
      setCourseToRemove(null);
      setIsRemovingCourse(false);
    }
  }, [isOpen]);

  if (!isOpen || !student) return null;

  const avatarUrl = getAvatarUrl?.(student) || '/Resources/imagine/default_avatar/default_avatar.jpeg';
  const normalizedTutorId = typeof tutorId === 'string' ? tutorId.trim() : '';

  const isTutorCreatedCourse = (course) => {
    const creator = typeof course?.created_by === 'string' ? course.created_by.trim() : '';
    return !!normalizedTutorId && creator === normalizedTutorId;
  };

  const getCourseTimestamp = (course) => {
    const rawTime =
      course?.generated_at ||
      course?.created_at ||
      course?.updated_at ||
      course?.course_contents?.internal_meta?.created_at;
    const timestamp = rawTime ? Date.parse(rawTime) : Number.NEGATIVE_INFINITY;
    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
  };

  const sortByNewest = (a, b) => getCourseTimestamp(b) - getCourseTimestamp(a);

  const assignedCourses = courses.filter(isTutorCreatedCourse).sort(sortByNewest);
  const otherCourses = courses.filter((course) => !isTutorCreatedCourse(course)).sort(sortByNewest);
  const studentDrafts = drafts
    .filter((draft) => draft?.student_id === student?.student_id)
    .sort((a, b) => Date.parse(b?.updated_at || b?.created_at || 0) - Date.parse(a?.updated_at || a?.created_at || 0));

  const isCourseRemovable = (course) => Number(course?.completed_count || 0) === 0;

  const handleConfirmRemoveCourse = async () => {
    if (!courseToRemove || !onRemoveCourse) return;

    setIsRemovingCourse(true);
    try {
      await onRemoveCourse(courseToRemove);
      setCourseToRemove(null);
    } finally {
      setIsRemovingCourse(false);
    }
  };

  const renderCourseList = (courseList, emptyText, emptyHint, allowRemoval = false) => {
    if (courseList.length === 0) {
      return (
        <div className="manage-student-empty">
          <p>{emptyText}</p>
          {emptyHint ? <p className="manage-student-empty-hint">{emptyHint}</p> : null}
        </div>
      );
    }

    return (
      <div className="manage-student-course-list">
        {courseList.map((course) => {
          const totalSentences = course.length || course.course_contents?.length_of_content || 0;
          const completedSentences = course.completed_count || 0;
          const progressPercent = totalSentences > 0 
            ? Math.round((completedSentences / totalSentences) * 100) 
            : 0;
          const canRemoveCourse = allowRemoval && isCourseRemovable(course);

          return (
            <div key={course.cid} className="manage-student-course-card">
              <div className="manage-student-course-info">
                <span className="manage-student-course-icon">📖</span>
                <div className="manage-student-course-details">
                  <span className="manage-student-course-name">
                    {course.title || course.course_name || 'Untitled Course'}
                  </span>
                  <div className="manage-student-course-progress">
                    <div className="manage-student-progress-bar">
                      <div 
                        className="manage-student-progress-fill" 
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <span className="manage-student-progress-text">
                      {progressPercent}% ({completedSentences}/{totalSentences})
                    </span>
                  </div>
                </div>
              </div>
              <div className="manage-student-course-actions">
                <button
                  type="button"
                  className="manage-student-course-btn view"
                  onClick={() => setViewingCourse(course)}
                >
                  View
                </button>
                {canRemoveCourse ? (
                  <button
                    type="button"
                    className="manage-student-course-btn remove"
                    onClick={() => setCourseToRemove(course)}
                    aria-label={`Remove ${course.title || course.course_name || 'course'}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDraftList = () => {
    if (studentDrafts.length === 0) {
      return (
        <div className="manage-student-empty">
          <p>No course drafts yet.</p>
          <p className="manage-student-empty-hint">Generate from "Assign New Course", then reopen from this tab.</p>
        </div>
      );
    }

    return (
      <div className="manage-student-course-list">
        {studentDrafts.map((draft) => {
          const requestJson = draft?.request_json || {};
          const generatedJson = draft?.generated_json || {};
          const generatedCount = Array.isArray(generatedJson?.content) ? generatedJson.content.length : 0;
          const updatedAt = draft?.updated_at || draft?.created_at;
          const title = requestJson?.course_name || 'Untitled Draft';
          return (
            <div key={draft.id} className="manage-student-course-card">
              <div className="manage-student-course-info">
                <span className="manage-student-course-icon">Draft</span>
                <div className="manage-student-course-details">
                  <span className="manage-student-course-name">{title}</span>
                  <span className="manage-student-draft-meta">
                    {generatedCount > 0 ? `${generatedCount} generated sentences` : 'Generation pending'} • {updatedAt ? new Date(updatedAt).toLocaleString() : 'No date'}
                  </span>
                </div>
              </div>
              <div className="manage-student-course-actions">
                <button
                  type="button"
                  className="manage-student-course-btn view"
                  onClick={() => onOpenDraft?.(draft)}
                >
                  Open Draft
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="manage-student-overlay" onClick={onClose}>
        <div className="manage-student-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="manage-student-header">
            <div className="manage-student-title-row">
              <h2 className="manage-student-title">Manage Student</h2>
              <button type="button" className="manage-student-close" onClick={onClose}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            {/* Student Info */}
            <div className="manage-student-info">
              <img 
                src={avatarUrl} 
                alt="Avatar" 
                className="manage-student-avatar"
              />
              <div className="manage-student-details">
                <span className="manage-student-name">{student.username}</span>
                <span className="manage-student-meta">
                  {courses.length} {courses.length === 1 ? 'course' : 'courses'} total
                </span>
              </div>
            </div>
          </div>
  
          {/* Content */}
          <div className="manage-student-content">
            <div className="manage-student-tabs" role="tablist" aria-label="Course categories">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'assigned'}
                className={`manage-student-tab-btn ${activeTab === 'assigned' ? 'active' : ''}`}
                onClick={() => setActiveTab('assigned')}
              >
                <span>Assigned</span>
                <span className="manage-student-count-badge">{assignedCourses.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'other'}
                className={`manage-student-tab-btn ${activeTab === 'other' ? 'active' : ''}`}
                onClick={() => setActiveTab('other')}
              >
                <span>Other</span>
                <span className="manage-student-count-badge">{otherCourses.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'drafts'}
                className={`manage-student-tab-btn ${activeTab === 'drafts' ? 'active' : ''}`}
                onClick={() => setActiveTab('drafts')}
              >
                <span>Drafts</span>
                <span className="manage-student-count-badge">{studentDrafts.length}</span>
              </button>
            </div>

            {activeTab === 'assigned' && renderCourseList(
              assignedCourses,
              'No tutor-created courses assigned yet.',
              'Click "Assign New Course" to create and assign a course to this student.',
              true
            )}
            {activeTab === 'other' && renderCourseList(
              otherCourses,
              'No other courses found.',
              'This section includes courses created by other tutors or AI.'
            )}
            {activeTab === 'drafts' && renderDraftList()}
          </div>
  
          {/* Footer */}
          <div className="manage-student-footer">
            <button
              type="button"
              className="manage-student-assign-btn"
              onClick={() => onAssignCourse?.(null)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Assign New Course
            </button>
          </div>
        </div>
      </div>

      {/* Course Progress Modal */}
      <CourseProgressModal 
        isOpen={!!viewingCourse}
        onClose={() => setViewingCourse(null)}
        courseId={viewingCourse?.cid}
        studentId={student?.student_id}
      />

      <ConfirmDialog
        isOpen={!!courseToRemove}
        title="Remove Course"
        message={`Are you sure you want to remove "${courseToRemove?.title || courseToRemove?.course_name || 'this course'}"? This is only available before the student starts the course.`}
        onConfirm={handleConfirmRemoveCourse}
        onCancel={() => {
          if (!isRemovingCourse) {
            setCourseToRemove(null);
          }
        }}
        isPending={isRemovingCourse}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </>
  );
}
